import {formatReferenceCopy} from 'utils/typography';
import React, {useMemo} from "react";
import cx from "classnames";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import FormGroup from "@mui/material/FormGroup";
import Divider from "@mui/material/Divider";
import Grow from '@mui/material/Grow';

import {COLORS} from "../../../../../../../config/constants";

import ShowMoreMenu from "../../../../../../common/showMoreMenu";
import {
  setUserExtraServicesIds,
  useExtraServicesId,
  useLazyGetExtraServicesByOrderIdQuery,
  useRemoveExtraServicesByOrderIdMutation, useSetCompletedByOrderIdMutation, useSummaryCompleted,
} from "../../../../../../../api/calc";
import {useGetCalcIdFromRoute} from "../../../../../../../utils/hooks";

import CompanyCardServiceItem from "../company-card-service-item";
import {isCalculationViewModeError, numberWithSpaces} from 'utils/helpers';

import './additional-company-card.scss';

type Props = {
    isActive?: boolean;
    company: ExtraService;
    readOnly?: boolean;
    refetchGetExtraServices?(): void
    setInitialValuesExtraServices(values: number[]): void
}

const CompanyCard = ({isActive, company, readOnly = false, refetchGetExtraServices, setInitialValuesExtraServices}: Props) => {

  const { t } = useTranslation()

  const orderId = useGetCalcIdFromRoute()

  const userServices = useExtraServicesId();
  const [ setCompleted ] = useSetCompletedByOrderIdMutation();

  const [ removeExtraServices ] = useRemoveExtraServicesByOrderIdMutation()
  const [ triggerGetExtraServicesRequest ] = useLazyGetExtraServicesByOrderIdQuery()

  const dispatch = useDispatch()
  const summaryCompleted = useSummaryCompleted();

  const handleRemove = async () => {
    const companyServiceIds = new Set(company.services.map((service) => service.id));

    const filteredUserServices = userServices?.filter((id) => !companyServiceIds.has(id));

    if (isActive) {
      const result = await removeExtraServices({
        orderId,
        company_id: company.id,
      });

      if (isCalculationViewModeError(result)) {
        return;
      }

      await triggerGetExtraServicesRequest(orderId);

      // @ts-ignore
      setInitialValuesExtraServices(filteredUserServices);

      if (filteredUserServices?.length === 0 && summaryCompleted?.additional_services) {
        await setCompleted({
          orderId,
          additional_services: false,
        });
      }
    }

    // @ts-ignore
    dispatch(setUserExtraServicesIds(filteredUserServices));
  };


  const totalServicesCost = useMemo(() => {
    return company.services
      .filter(service => userServices?.includes(service.id))
      .reduce((sum, service) => sum + service.price, 0);
  }, [ company.services, userServices ]);

  const shouldShowTotalServicesCost = totalServicesCost > 0;

  return <Card className={cx('additional-company-card', {
    'additional-company-card_active': isActive,
  })} sx={{
    background: isActive ? COLORS.LIME : COLORS.WHITE,
    borderRadius: 0,
    boxShadow: 'none',
    overflow: 'visible'
  }}>
    <CardContent sx={{
      padding: 0,
      '&:last-child': {
        paddingBottom: 0,
      },
    }}>
      <div className='additional-company-card__title'>
        <div className='additional-company-card__left'>
          <Typography component='span' variant='h6' className='additional-company-card__logo' sx={{
            color: COLORS.WHITE,
            fontSize: '24px',
            lineHeight: '32px',
          }}>{company.name[0]}</Typography>
          <Typography variant='subtitle2' sx={{
            color: COLORS.BLACK_DARK,
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '24px',
            letterSpacing: 0,
          }}>
            {formatReferenceCopy(company.name)}
          </Typography>
        </div>
        {shouldShowTotalServicesCost &&
            <Grow
              in={shouldShowTotalServicesCost}
              timeout={200}
              mountOnEnter
              unmountOnExit
            >
              <Typography
                variant='h6'
                component='div'
                sx={{
                  color: COLORS.BLACK_DARK,
                  fontSize: '20px',
                  fontWeight: 400,
                  lineHeight: '26px',
                  letterSpacing: 0,
                  flexShrink: 0,
                  gap: '8px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                className='additional-company-card__right'
              >
                <Grow
                  key={totalServicesCost}
                  in
                  timeout={150}
                >
                  <Typography component='span' variant='inherit'>{numberWithSpaces(totalServicesCost)} USD</Typography>
                </Grow>
                <ShowMoreMenu menuItems={[ {
                  title: t('common__text__remove'),
                  onClick: handleRemove
                } ]}/>
              </Typography>
            </Grow>}
      </div>
      <FormGroup sx={{margin: '0 24px 24px'}}>
        {company.services.map((service: ExtraServiceItem, index) =>
          <CompanyCardServiceItem
            service={service}
            index={index}
            key={service.id}
            isActive={userServices?.includes(service.id)}
            isMainPage={isActive}
            refetchGetExtraServices={refetchGetExtraServices}
          />)}
        <Divider sx={{ marginTop: 0 }} />
      </FormGroup>
    </CardContent>
  </Card>
}

export default CompanyCard
